import {
  Box,
  Button,
  Card,
  Flex,
  Grid,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react'
import { keepPreviousData, useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import axios from 'axios'
import React, { useMemo, useState } from 'react'
import { CircleFlag } from 'react-circle-flags'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { fetchJobList } from '../../api/classification/classification'
import {
  deleteEmployee,
  disableEmployee,
  exportEmployeesToCsv,
  exportEmployeesToXls,
  fetchEmployeeList,
  fetchTeamList,
  inviteEmployee,
  restoreEmployee,
} from '../../api/workforce/workforce'
import {
  ChevronDownIcon,
  ExportIcon,
  PlusIcon,
  RefreshCCW01Icon,
  Trash04Icon,
} from '../../assets/icons'
import { getCountryNameByCode, phoneNumbers } from '../countries/PhoneDial'
import handleToast from '../HandleToast'
import AvatarCell from './base/AvatarCell'
import AddEmployeesModal from '../modals/addEmployees'
import DiscardModal from '../modals/discardModal'
import ActiveBar from './base/ActiveBar'
import FilterButton from './base/FilterButton'
import LoadingTable from './base/LoadingTable'
import MasterTable, { Column, RowData } from './base/MasterTable'
import Pagination from './base/Pagination'
import SearchInput from './base/SearchInput'
import { prefetchEmployeeRowIntent } from '../../lib/performance/prefetch'
import { useListInteractionMetric } from '../../lib/performance/monitoring'

// prop for passing page where user was before navigating away
interface PeopleTableProps {
  initialPage?: number
  onAddEmployee?: (employee: Record<string, unknown>) => void
}

const PeopleTable: React.FC<PeopleTableProps> = ({ initialPage = 1, onAddEmployee }) => {
  const { t } = useTranslation()

  const [selectedRowsActive, setSelectedRowsActive] = useState<(number | string)[]>([])
  const [selectedRowsDisabled, setSelectedRowsDisabled] = useState<(number | string)[]>([])
  const [isAddEmployeesModalOpen, setIsAddEmployeesModalOpen] = useState(false)
  const [selectedTab, setSelectedTab] = useState('active')
  const [showDiscardModal, setShowDiscardModal] = useState(false)
  const [modalAction, setModalAction] = useState<'disable' | 'delete' | 'restore'>('disable')
  const [filters, setFilters] = useState<{ [key: string]: string[] }>({ active: ['true'] })
  const [deactivatedData, setDeactivatedData] = useState<{ [key: string]: string }>({
    reason: '',
    status_exit_evaluation: '',
    status_access_it: '',
    status_keys: '',
    status_company_devices: '',
    last_day: '',
  })
  const [isExporting, setIsExporting] = useState(false)
  const [isMutating, setIsMutating] = useState(false)

  const navigate = useNavigate()
  const toast = useToast()
  const [sortBy, setSortBy] = useState<'recent' | 'alpha_asc' | 'alpha_desc'>('recent')
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false)

  const queryClient = useQueryClient()
  const [page, setPage] = useState(initialPage) // either 1 or the page user was on before navigating away
  const [searchTerm, setSearchTerm] = useState('')
  const itemsPerPage = 15

  // --- Sort options ---
  const SORT_OPTIONS = useMemo(
    () =>
      [
        { id: 'recent', label: t('workforce.organization.sortOptions.recentlyAdded') },
        { id: 'alpha_asc', label: t('workforce.organization.sortOptions.alphabeticalAZ') },
        { id: 'alpha_desc', label: t('workforce.organization.sortOptions.alphabeticalZA') },
      ] as const,
    [t],
  )

  const isSortModified = sortBy !== 'recent'

  // --- Filter state ---
  const updateFilter = (key: string, values: string[]) => {
    setFilters((prev) => {
      const next = { ...prev }
      if (values.length === 0) delete next[key]
      else next[key] = values
      return next
    })
    setPage(1)
  }

  // --- Infinite scroll filter data: Teams ---
  const [teamsFilterSearch, setTeamsFilterSearch] = useState('')
  const teamsFilterQuery = useInfiniteQuery({
    queryKey: ['filter-teams', 10, teamsFilterSearch],
    queryFn: ({ pageParam }) =>
      fetchTeamList({ search: teamsFilterSearch, page: pageParam, pageSize: 10, filters: {}, fields: 'id,name', expand: '' }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.has_next ? allPages.length + 1 : undefined,
  })
  const teamsFilterList = useMemo(
    () =>
      (teamsFilterQuery.data?.pages ?? []).flatMap((p: { data?: { id: string; name: string }[] }) =>
        Array.isArray(p?.data) ? p.data.map((team) => ({ id: team.id, name: team.name })) : [],
      ),
    [teamsFilterQuery.data?.pages],
  )

  // --- Infinite scroll filter data: Job Titles ---
  const [positionsFilterSearch, setPositionsFilterSearch] = useState('')
  const positionsFilterQuery = useInfiniteQuery({
    queryKey: ['filter-positions', 10, positionsFilterSearch],
    queryFn: ({ pageParam }) =>
      fetchJobList({ search: positionsFilterSearch, page: pageParam, pageSize: 10, fields: 'id,title' }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.has_next ? allPages.length + 1 : undefined,
  })
  const positionsFilterList = useMemo(
    () =>
      (positionsFilterQuery.data?.pages ?? []).flatMap((p: { data?: { id: string; title: string }[] }) =>
        Array.isArray(p?.data) ? p.data.map((pos) => ({ id: pos.id, name: pos.title })) : [],
      ),
    [positionsFilterQuery.data?.pages],
  )

  // --- Static filter data: Country ---
  const countryFilterList = useMemo(() => phoneNumbers.map((c) => ({ id: c.code, name: c.name })), [])

  const ordering =
    sortBy === 'recent' ? '-created_at' : sortBy === 'alpha_asc' ? 'first_name,last_name' : '-first_name,-last_name'
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters])

  const people = useQuery({
    queryKey: ['people', page, searchTerm, filtersKey, sortBy],
    // keep recently prefetched pages fresh for a short time to avoid immediate refetch
    staleTime: 60 * 1000, // 1 minute
    placeholderData: keepPreviousData,
    queryFn: () =>
      fetchEmployeeList({
        search: searchTerm,
        page,
        pageSize: itemsPerPage,
        filters,
        ordering, // pass ordering based on sortBy state, so backend returns sorted data
        // only request the fields needed to render the table columns
        expand: 'teams.id,teams.name,managed_teams,current_position',
        fields:
          'id,first_name,last_name,full_name,profile_picture,current_position.id,current_position.title,teams.id,teams.name,managed_teams.id,managed_teams.name,country_of_work,work_email',
      }),
  })

  useListInteractionMetric(
    'list.people.interaction',
    people.isFetching,
    `${page}|${searchTerm}|${filtersKey}|${sortBy}`,
    {
      page,
      searchLength: searchTerm.length,
      filterCount: Object.keys(filters).length,
      sortBy,
    },
  )

  // Prefetch next page when current page looks full (likely more data)
  React.useEffect(() => {
    const currentCount = people.data?.data?.length ?? 0
    if (currentCount < itemsPerPage) return

    const nextPage = page + 1

    queryClient.prefetchQuery({
      queryKey: ['people', nextPage, searchTerm, filtersKey, sortBy],
      // keep prefetched data fresh for the same duration so it won't be refetched on mount
      staleTime: 60 * 1000,
      queryFn: () =>
        fetchEmployeeList({
          search: searchTerm,
          page: nextPage,
          pageSize: itemsPerPage,
          filters,
          ordering,
          expand: 'teams.id,teams.name,managed_teams,current_position',
          fields:
            'id,first_name,last_name,full_name,profile_picture,current_position.id,current_position.title,teams.id,teams.name,managed_teams.id,managed_teams.name,country_of_work,work_email',
        }),
    })
  }, [people.data, page, searchTerm, filters, filtersKey, sortBy, queryClient, itemsPerPage])

  const tableData = people?.data?.data || []

  const disableMutation = useMutation({
    mutationFn: ({ employeeId, deactivatedData }: { employeeId: string; deactivatedData: { [key: string]: string } }) =>
      disableEmployee(employeeId, deactivatedData),
  })

  const inviteMutation = useMutation({
    mutationFn: (employee: { id: string }) => inviteEmployee(employee),
  })

  const restoreMutation = useMutation({
    mutationFn: restoreEmployee,
  })

  const deleteMutation = useMutation({
    mutationFn: deleteEmployee,
  })

  const columns: Column[] = [
    { label: '', accessor: 'select', type: 'select', width: '5%' },
    {
      label: t('common.fields.name'),
      accessor: 'name',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <AvatarCell image={row.profile_picture} name={`${row.first_name} ${row.last_name}`} />
      ),
    },
    {
      label: t('common.fields.jobTitle'),
      accessor: 'current_position',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <Text textStyle="l-xs" isTruncated>
          {row.current_position?.title || t('common.labels.na')}
        </Text>
      ),
    },
    {
      label: t('workforce.tabs.teams'),
      accessor: 'teams',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => {
        const membershipTeamNames = row.teams?.map((team: { name: string }) => team.name) || []

        const managedTeamNames = row.managed_teams?.map((team: { name: string }) => team.name) || []

        const teamNames = Array.from(new Set([...membershipTeamNames, ...managedTeamNames]))

        return (
          <Text textStyle="l-xs" isTruncated>
            {teamNames.length > 1 ? (
              <>
                {teamNames.slice(0, 1).join(', ')}
                <Text as="span" color="solid.400" textStyle="l-xs" ml="8px">
                  {t('common.labels.moreCount', { count: teamNames.length - 1 })}
                </Text>
              </>
            ) : (
              teamNames.length !== 0 ? teamNames.join(', ') : t('common.labels.na')
            )}
          </Text>
        )
      },
    },
    {
      label: t('workforce.people.countryOfWork'),
      accessor: 'country_of_work',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => (
        <Flex alignItems="center">
          {row.country_of_work && (
            <CircleFlag
              countryCode={row.country_of_work?.toLowerCase()}
              style={{ width: '24px', height: '24px', marginRight: '8px' }}
            />
          )}
          <Text isTruncated textStyle="l-xs">
            {row?.country_of_work ? getCountryNameByCode(row?.country_of_work) : t('common.labels.na')}
          </Text>
        </Flex>
      ),
    },
    {
      label: t('common.fields.email'),
      accessor: 'work_email',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => <Text textStyle="l-xs" isTruncated>{row.work_email}</Text>,
    },
    { label: '', accessor: 'actions', type: 'actions' },
  ]

  const handleSearch = (searchTerm: string) => {
    setPage(1)
    setSearchTerm(searchTerm)
  }

  // Helper to snapshot and optimistically remove rows from the current page cache
  const optimisticRemoveRows = (ids: (number | string)[]) => {
    const key = ['people', page, searchTerm, filters, sortBy]
    const previous = queryClient.getQueryData(key)
    queryClient.setQueryData(key, (old: any) => {
      if (!old) return old
      const idSet = new Set(ids.map(String))
      return {
        ...old,
        data: old.data?.filter((row: RowData) => !idSet.has(String(row.id))),
        count: Math.max(0, (old.count ?? 0) - ids.length),
      }
    })
    return { key, previous }
  }

  const handleDisable = async () => {
    const ids = [...selectedRowsActive]
    if (ids.length === 0) return
    setIsMutating(true)

    // Optimistic: remove from table immediately
    const snapshot = optimisticRemoveRows(ids)

    setSelectedRowsActive([])
    setShowDiscardModal(false)

    try {
      await Promise.all(
        ids.map((employeeId) =>
          disableMutation.mutateAsync({
            employeeId: employeeId.toString(),
            deactivatedData,
          }),
        ),
      )
      queryClient.invalidateQueries({ queryKey: ['people'] })
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      handleToast(
        toast,
        true,
        t('workforce.messages.disableSuccess'),
        t('workforce.messages.disableSuccessMessage', { count: ids.length }),
      )
    } catch (err) {
      // Rollback optimistic update
      queryClient.setQueryData(snapshot.key, snapshot.previous)
      setSelectedRowsActive(ids)
      handleToast(
        toast,
        false,
        t('workforce.messages.disableFailed'),
        t('workforce.messages.disableFailedMessage'),
      )
    } finally {
      setIsMutating(false)
    }
  }

  const handleExportClick = () => {
    setIsExportDropdownOpen(!isExportDropdownOpen)
  }

  const exportPeopleToXls = async () => {
    try {
      setIsExporting(true)

      const selectedIds = selectedTab === 'active' ? selectedRowsActive : selectedRowsDisabled

      const exportFilters: { [key: string]: string[] } = {
        ...filters,
        active: [selectedTab === 'active' ? 'true' : 'false'],
      }

      await exportEmployeesToXls({
        search: searchTerm,
        filters: exportFilters,
        ordering,
        ids: selectedIds.length > 0 ? selectedIds : undefined,
      })

      handleToast(
        toast,
        true,
        t('workforce.messages.exportSuccess'),
        t('workforce.messages.exportSuccessMessage', { format: 'XLS' }),
      )
    } catch (e) {
      console.error(e)
      handleToast(
        toast,
        false,
        t('workforce.messages.exportFailed'),
        t('workforce.messages.exportFailedMessage'),
      )
    } finally {
      setIsExporting(false)
    }
  }

  const exportPeopleToCsv = async () => {
    try {
      setIsExporting(true)

      const selectedIds = selectedTab === 'active' ? selectedRowsActive : selectedRowsDisabled

      const exportFilters: { [key: string]: string[] } = {
        ...filters,
        active: [selectedTab === 'active' ? 'true' : 'false'],
      }

      await exportEmployeesToCsv({
        search: searchTerm,
        filters: exportFilters,
        ordering,
        ids: selectedIds.length > 0 ? selectedIds : undefined,
      })

      handleToast(
        toast,
        true,
        t('workforce.messages.exportSuccess'),
        t('workforce.messages.exportSuccessMessage', { format: 'CSV' }),
      )
    } catch (e) {
      console.error(e)
      handleToast(
        toast,
        false,
        t('workforce.messages.exportFailed'),
        t('workforce.messages.exportFailedMessage'),
      )
    } finally {
      setIsExporting(false)
    }
  }

  // function to extract error message from backend response
  const getInviteErrorMessage = (error: unknown): string => {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data as Record<string, unknown>

      if (data?.code === 'INVITE_EXPIRED') {
        return t('workforce.messages.inviteExpired')
      }
      if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'string') {
        return data[0]
      }
      if (typeof data === 'string') {
        return data
      }
      if (data?.detail && typeof data.detail === 'string') {
        return data.detail
      }
      if (data?.error && typeof data.error === 'string') {
        return data.error
      }
      if (data?.message && typeof data.message === 'string') {
        return data.message
      }

      if (!error.response) {
        return t('workforce.messages.networkError')
      }

      return t('workforce.messages.requestFailed', { code: error.response.status })
    }

    const msg = (error as { message?: string })?.message
    return msg || t('workforce.messages.somethingWentWrong')
  }

  const handleInvite = () => {
    selectedRowsActive.forEach((employeeId) => {
      const employee = tableData.find((row: RowData) => row.id === employeeId)
      const displayName = employee?.full_name || t('common.fields.user')

      inviteMutation.mutate(
        { id: employeeId.toString() },
        {
          onSuccess: () => {
            handleToast(
              toast,
              true,
              t('workforce.messages.inviteSuccess'),
              t('workforce.messages.inviteSuccessMessage', { name: displayName }),
            )
            setSelectedRowsActive([])
          },
          onError: (e) => {
            handleToast(toast, false, t('workforce.messages.inviteFailed'), getInviteErrorMessage(e))
          },
        },
      )
    })
  }

  const handleOpenNewTab = (row: RowData) => {
    const path = `/user/workforce/people/${row.id}`
    window.open(path, '_blank')
  }

  const handleRowClick = (row: RowData) => {
    navigate(`/user/workforce/people/${row.id}`, { state: { from: page } }) // pass the current page
  }

  const handleRestore = async () => {
    const ids = [...selectedRowsDisabled]
    if (ids.length === 0) return
    setIsMutating(true)
    try {
      await Promise.all(ids.map((employeeId) => restoreMutation.mutateAsync(employeeId.toString())))
      queryClient.invalidateQueries({ queryKey: ['people'] })
      queryClient.invalidateQueries({ queryKey: ['disabledPeople'] })
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      handleToast(
        toast,
        true,
        t('workforce.messages.restoreSuccess'),
        t('workforce.messages.restoreSuccessMessage', { count: ids.length }),
      )
      setSelectedRowsDisabled([])
      setShowDiscardModal(false)
    } catch (err) {
      queryClient.invalidateQueries({ queryKey: ['people'] })
      handleToast(
        toast,
        false,
        t('workforce.messages.restoreFailed'),
        t('workforce.messages.restoreFailedMessage'),
      )
    } finally {
      setIsMutating(false)
    }
  }

  const handleDelete = async () => {
    const ids = [...selectedRowsDisabled]
    if (ids.length === 0) return
    setIsMutating(true)

    // Optimistic: remove from table immediately
    const snapshot = optimisticRemoveRows(ids)

    setSelectedRowsDisabled([])
    setShowDiscardModal(false)

    try {
      await Promise.all(ids.map((employeeId) => deleteMutation.mutateAsync(employeeId.toString())))
      queryClient.invalidateQueries({ queryKey: ['people'] })
      queryClient.invalidateQueries({ queryKey: ['disabledPeople'] })
      handleToast(
        toast,
        true,
        t('workforce.messages.deleteSuccess'),
        t('workforce.messages.deleteSuccessMessage', { count: ids.length }),
      )
    } catch (err) {
      // Rollback optimistic update
      queryClient.setQueryData(snapshot.key, snapshot.previous)
      setSelectedRowsDisabled(ids)
      handleToast(
        toast,
        false,
        t('workforce.messages.deleteFailed'),
        t('workforce.messages.deleteFailedMessage'),
      )
    } finally {
      setIsMutating(false)
    }
  }

  const handleSelectRow = (id: number | string, isChecked: boolean) => {
    if (selectedTab === 'active') {
      if (isChecked) {
        setSelectedRowsActive((prev) => [...prev, id])
      } else {
        setSelectedRowsActive((prev) => prev.filter((rowId) => rowId !== id))
      }
    } else {
      if (isChecked) {
        setSelectedRowsDisabled((prev) => [...prev, id])
      } else {
        setSelectedRowsDisabled((prev) => prev.filter((rowId) => rowId !== id))
      }
    }
  }

  const handleSelectAll = (isChecked: boolean) => {
    if (selectedTab === 'active') {
      if (isChecked) {
        setSelectedRowsActive(tableData.map((row: RowData) => row.id))
      } else {
        setSelectedRowsActive([])
      }
    } else {
      if (isChecked) {
        setSelectedRowsDisabled(tableData.map((row: RowData) => row.id))
      } else {
        setSelectedRowsDisabled([])
      }
    }
  }

  return (
    <Card width="100%" p="0">
      <Grid>
          <ActiveBar selectedTab={selectedTab} onSelectTab={(tab: string) => {
            setSelectedTab(tab)
            setSelectedRowsActive([])
            setSelectedRowsDisabled([])
          }} setFilters={setFilters} />
          <Flex justifyContent="space-between" alignItems="center" height="64px" flexWrap="wrap" gap={4} minW={0} px="20px">
            <SearchInput onSearch={handleSearch} />

            <Flex gap="8px" flexShrink={0}>
              {selectedTab === 'active' &&
                (selectedTab === 'active' ? selectedRowsActive : selectedRowsDisabled).length > 0 ? (
                <>
                  <Button
                    onClick={handleInvite}
                    _hover={{
                      borderColor: 'solid.100',
                      outline: 'none',
                    }}
                    variant="white"
                      disabled={!tableData.length || isExporting || isMutating}
                      size="sm"
                      h="32px"
                      px="14px"
                      fontSize="13px"
                      fontWeight="600"
                      borderRadius="8px"
                  >
                    {t('workforce.people.inviteCount', { count: selectedRowsActive.length })}
                  </Button>
                  <Button
                    onClick={() => {
                      setModalAction('disable')
                      setShowDiscardModal(true)
                    }}
                    variant="error"
                    disabled={isMutating}
                    size="sm"
                    h="32px"
                    px="14px"
                    fontSize="13px"
                    fontWeight="600"
                    borderRadius="8px"
                  >
                    {t('workforce.people.disableCount', { count: selectedRowsActive.length })}
                  </Button>
                </>
              ) : selectedTab === 'disabled' && selectedRowsDisabled.length > 0 ? (
                <HStack spacing="12px">
                  <Button
                    onClick={() => {
                      setModalAction('restore')
                      setShowDiscardModal(true)
                    }}
                    variant="white"
                    disabled={!tableData.length || isExporting || isMutating}
                    size="sm"
                    h="32px"
                    px="14px"
                    fontSize="13px"
                    fontWeight="600"
                    borderRadius="8px"
                  >
                    <Icon  w="12px" h="12px" viewBox="0 0 24 24" fill="none" color="black" mr={2}>
                      <RefreshCCW01Icon />
                    </Icon>
                    {t('workforce.people.restoreCount', { count: selectedRowsDisabled.length })}
                  </Button>
                  <Button
                    onClick={() => {
                      setModalAction('delete')
                      setShowDiscardModal(true)
                    }}
                    variant="error" 
                    size="sm"
                    h="32px"
                    px="14px"
                    fontSize="13px"
                    fontWeight="600"
                    borderRadius="8px"
                  >
                    <Icon fill="none" color="error.100" mr={2}>
                      <Trash04Icon />
                    </Icon>
                    {t('workforce.people.deleteCount', { count: selectedRowsDisabled.length })}
                  </Button>
                </HStack>
              ) : (selectedTab === 'active' ? selectedRowsActive : selectedRowsDisabled).length > 0 ? (
                <Button
                  onClick={() => {
                    setModalAction('delete')
                    setShowDiscardModal(true)
                  }}
                  variant="error"
                >
                  <Icon fill="none" color="error.100" mr={2}>
                    <Trash04Icon />
                  </Icon>
                  {t('workforce.people.deleteCount', {
                    count: (selectedTab === 'active' ? selectedRowsActive : selectedRowsDisabled).length,
                  })}
                </Button>
              ) : (
                <>
                  <FilterButton
                    label={t('common.fields.team')}
                    options={teamsFilterList}
                    onApply={(selected) => updateFilter('teams', selected)}
                    search={teamsFilterSearch}
                    setSearch={setTeamsFilterSearch}
                    onLoadMore={() => teamsFilterQuery.fetchNextPage()}
                    hasMore={!!teamsFilterQuery.hasNextPage}
                    isLoadingMore={teamsFilterQuery.isFetchingNextPage}
                    isLoading={teamsFilterQuery.isFetching && !teamsFilterQuery.isFetchingNextPage}
                  />
                  <FilterButton
                    label={t('common.fields.jobTitle')}
                    options={positionsFilterList}
                    onApply={(selected) => updateFilter('current_position', selected)}
                    search={positionsFilterSearch}
                    setSearch={setPositionsFilterSearch}
                    onLoadMore={() => positionsFilterQuery.fetchNextPage()}
                    hasMore={!!positionsFilterQuery.hasNextPage}
                    isLoadingMore={positionsFilterQuery.isFetchingNextPage}
                    isLoading={positionsFilterQuery.isFetching && !positionsFilterQuery.isFetchingNextPage}
                  />
                  <FilterButton
                    label={t('common.fields.country')}
                    options={countryFilterList}
                    onApply={(selected) => updateFilter('country_of_work', selected)}
                  />

                  {/* Sort menu */}
                  <Menu placement="bottom-end">
                    <MenuButton
                      as={Button}
                      display="flex"
                      alignItems="center"
                      gap={2}
                      sx={{
                        bg: isSortModified ? 'purple.400' : 'transparent',
                        color: isSortModified ? 'white' : 'black',
                        fontWeight: isSortModified ? '600' : '400',
                      }}
                      variant="white"
                      disabled={!tableData.length || isExporting}
                      size="sm"
                      h="32px"
                      px="14px"
                      fontSize="13px"
                      fontWeight="600"
                      borderRadius="8px"
                    >
                      {SORT_OPTIONS.find((o) => o.id === sortBy)?.label ?? t('common.actions.sort')}
                      <Icon width="12px" height="12px" fill="none" color="solid.500" ml={2}>
                        <ChevronDownIcon />
                      </Icon>
                    </MenuButton>
                    <MenuList minW="220px">
                      {SORT_OPTIONS.map((opt) => (
                        <MenuItem
                          key={opt.id}
                          onClick={() => {
                            setPage(1)
                            setSortBy(opt.id)
                          }}
                          fontWeight={sortBy === opt.id ? '600' : '400'}
                        >
                          {opt.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>

                  {/* Export button */}
                  <Box position="relative">
                    <Button
                      variant="white"
                      onClick={handleExportClick}
                      disabled={!tableData.length || isExporting}
                      size="sm"
                      h="32px"
                      px="14px"
                      fontSize="13px"
                      fontWeight="600"
                      borderRadius="8px"
                    >
                      <Icon w="12px" h="12px" viewBox="0 0 24 24" fill="none" color="black" mr={2}>
                        <ExportIcon />
                      </Icon>
                      {isExporting
                        ? t('common.states.exporting')
                        : (selectedTab === 'active' ? selectedRowsActive : selectedRowsDisabled).length > 0
                          ? t('workforce.people.exportSelected', {
                              count: (selectedTab === 'active' ? selectedRowsActive : selectedRowsDisabled).length,
                            })
                          : t('workforce.people.exportAll')}
                    </Button>
                    {isExportDropdownOpen && (
                      <Box
                        position="absolute"
                        right="0"
                        width="196px"
                        height="92px"
                        borderRadius="8px"
                        mt={2}
                        opacity="1"
                        boxShadow="md"
                        backgroundColor="white"
                        zIndex="1000"
                        padding={0}
                      >
                        <VStack align="stretch" spacing={0} height="100%">
                          <Button
                            variant={'dropdown'}
                            height="50%"
                            borderRadius="8px 8px 0 0"
                            onClick={exportPeopleToXls}
                            fontSize="sm"
                          >
                            {t('workforce.people.exportAsXLS')}
                          </Button>
                          <Button
                            variant={'dropdown'}
                            height="50%"
                            borderRadius="0 0 8px 8px"
                            onClick={exportPeopleToCsv}
                            fontSize="sm"
                          >
                            {t('workforce.people.exportAsCSV')}
                          </Button>
                        </VStack>
                      </Box>
                    )}
                  </Box>

                  {/* Add button */}
                  {onAddEmployee && (
                    <Button
                      variant="mono"
                      onClick={() => setIsAddEmployeesModalOpen(true)}
                      size="sm"
                      h="32px"
                      px="14px"
                      fontSize="13px"
                      fontWeight="600"
                      borderRadius="8px"
                    >
                      <Icon w="12px" h="12px" viewBox="0 0 24 24" fill="none" color="white">
                        <PlusIcon />
                      </Icon>
                      {t('workforce.people.addNewPeople')}
                    </Button>
                  )}
                </>
              )}
            </Flex>
          </Flex>
          {people.isLoading ? (
            <LoadingTable columns={columns} />
          ) : (
            <Box
  minHeight="58vh"
  overflowX="auto"
  overflowY="visible"
>
              <MasterTable
                columns={columns}
                data={tableData}
                onRowIntent={(row) => prefetchEmployeeRowIntent(queryClient, row.id)}
                onRowClick={handleRowClick}
                onOpenNewTab={handleOpenNewTab}
                selectedRows={selectedTab === 'active' ? selectedRowsActive : selectedRowsDisabled}
                onSelectAll={handleSelectAll}
                onSelectRow={handleSelectRow}
                allSelected={
                  tableData.length > 0 &&
                  (selectedTab === 'active'
                    ? selectedRowsActive.length === tableData.length
                    : selectedRowsDisabled.length === tableData.length)
                }
                isIndeterminate={
                  tableData.length > 0 &&
                  (selectedTab === 'active'
                    ? selectedRowsActive.length > 0 && selectedRowsActive.length < tableData.length
                    : selectedRowsDisabled.length > 0 && selectedRowsDisabled.length < tableData.length)
                }
                tableType="people"
              />
            </Box>
          )}
          <Pagination
            currentPage={page}
            itemsPerPage={itemsPerPage}
            maxPage={people?.data?.max_page || 0}
            totalItems={people?.data?.count || 0}
            handlePreviousPage={() => setPage(page - 1)}
            handleNextPage={() => setPage(page + 1)}
            handlePageClick={(page: number) => setPage(page)}
          />
      </Grid>
      <DiscardModal
        show={showDiscardModal}
        handleClose={() => {
          if (!isMutating) setShowDiscardModal(false)
        }}
        handleDelete={modalAction === 'disable' ? handleDisable : modalAction === 'restore' ? handleRestore : handleDelete}
        action={modalAction}
        selectedRowsCount={selectedTab === 'active' ? selectedRowsActive.length : selectedRowsDisabled.length}
        deactivatedData={deactivatedData}
        setDeactivatedData={setDeactivatedData}
        isLoading={isMutating}
      />
      {onAddEmployee && (
        <AddEmployeesModal
          isOpen={isAddEmployeesModalOpen}
          onClose={() => setIsAddEmployeesModalOpen(false)}
          onAddEmployee={(employee: Record<string, unknown>) => {
            // Optimistic: prepend new employee to current page cache
            const key = ['people', page, searchTerm, filters, sortBy]
            queryClient.setQueryData(key, (old: any) => {
              if (!old) return old
              return {
                ...old,
                data: [employee, ...(old.data ?? [])],
                count: (old.count ?? 0) + 1,
              }
            })
            onAddEmployee(employee)
          }}
        />
      )}
    </Card>
  )
}

export default PeopleTable
